import React, { useEffect, useState, useContext } from 'react';
import {
	useNavigate, useParams, useLocation, Outlet,
} from 'react-router-dom';

import { fetchData } from '../../helpers/methods';
import downloadFile from '../../helpers/download-file';
import { CustomerContext } from '../../contexts/customer-context';

import Breadcrumb from '../../component-library/breadcrumb';
import StatusIndicator from '../../component-library/status-indicator';
import SpinnerContainer from '../../component-library/spinner-container';
import ButtonIcon from '../../component-library/button-icon';
import Icon from '../../component-library/icon';

const Details = () => {
	const navigate = useNavigate();
	const {
		pathname,
		state,
	} = useLocation();
	const {
		visualConfig: {
			bundles: {
				all: bundlesConfig = {},
			} = {},
			specs: specsConfig = {},
		} = {},
		portalUIConfig: {
			detailsMenu: {
				bundles: bundlesUiMenuConfig = {},
				specs: specsUiMenuConfig = {},
			} = {},
			detailsGeneral: {
				specs: specsUiGeneralConfig = {},
			} = {},
		} = {},
		catalogBaseURI = '',
		customerCode = 'DEFAULT',
	} = useContext(CustomerContext);
	const { id } = useParams();
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(false);
	const [navItem, setNavItem] = useState('about');
	const [data, setData] = useState({});
	const [storefrontData, setStorefrontData] = useState({});
	const [breadcrumbData, setBreadcrumbData] = useState([
		{ name: 'Home', url: '/' },
		{ name: 'Catalog', url: '/catalog' },
		{ name: 'Details' },
	]);
	const [isFavourite, setIsFavourite] = useState(false);
	const [isSubscribed, setIsSubscribed] = useState(false);
	// eslint-disable-next-line no-unused-vars
	const [referrer, setReferrer] = useState(state?.from || '');
	const [downloading, setDownloading] = useState(false);
	const [displayConfig, setDisplayConfig] = useState({});
	const [uiMenuConfig, setUiMenuConfig] = useState({});
	const [uiGeneralConfig, setUiGeneralConfig] = useState({});
	const [objectType, setObjectType] = useState('specification');
	const [interfaceType, setInterfaceType] = useState('');

	const checkConfig = intType => {
		if (interfaceType === 'bundle') {
			setDisplayConfig(bundlesConfig);
			setUiMenuConfig(bundlesUiMenuConfig);
			setObjectType('bundle');
		} else {
			const interfaceTypeConfig = specsConfig[intType];
			if (interfaceTypeConfig) {
				setDisplayConfig(specsConfig[intType]);
				setUiMenuConfig(specsUiMenuConfig[intType]);
				setUiGeneralConfig(specsUiGeneralConfig[intType]);
			}
			setObjectType('specification');
		}
		setLoading(false);
	};

	const getData = () => {
		fetchData({
			uri: `/api/web/catalog/item/${id}/summary`,
			method: 'GET',
		})
			.then(({ ok, body }) => {
				if (ok) {
					const itemData = body;
					let newInterfaceType = 'bundle';
					if (itemData.storefront?.length > 0) {
						const storefrontArray = itemData.storefront.split(':');
						setStorefrontData(
							{
								runtime: storefrontArray[0],
								group: storefrontArray[1],
								api: storefrontArray[2],
							},
						);
					}
					if (itemData.interfaceType) {
						newInterfaceType = itemData.interfaceType.name.toLowerCase();
					}
					setData(itemData);
					setInterfaceType(newInterfaceType);
					checkConfig(newInterfaceType);
					setIsFavourite(itemData.userFavourite || false);
					setIsSubscribed(itemData.userWatched || false);
				} else {
					setLoading(false);
					setError(true);
				}
			})
			.catch(() => {
				setLoading(false);
				setError(true);
			});
	};

	const addFavourite = () => {
		fetchData({
			uri: `/api/web/my/store/favourites/${id}/add`,
			method: 'POST',
		})
			.then(({ ok }) => {
				if (ok) {
					setIsFavourite(true);
				}
			});
	};

	const removeFavourite = () => {
		fetchData({
			uri: `/api/web/my/store/favourites/${id}/remove`,
			method: 'POST',
		})
			.then(({ ok }) => {
				if (ok) {
					setIsFavourite(false);
				}
			});
	};

	const subscribeUpdates = () => {
		fetchData({
			uri: `/api/web/my/store/watch/${id}/add`,
			method: 'POST',
		})
			.then(({ ok }) => {
				if (ok) {
					setIsSubscribed(true);
				}
			});
	};

	const unsubscribeUpdates = () => {
		fetchData({
			uri: `/api/web/my/store/watch/${id}/remove`,
			method: 'POST',
		})
			.then(({ ok }) => {
				if (ok) {
					setIsSubscribed(false);
				}
			});
	};

	const downloadOpenApi = uri => {
		downloadFile(uri, () => setDownloading(true), () => setDownloading(false));
	};

	const viewInCatalog = () => {
		window.open(`${catalogBaseURI}/app/${objectType}/${id}`, '_blank');
	};

	useEffect(() => {
		let newActiveSection = 'about';
		let newBreadcrumbData = [
			{ name: 'Home', url: '/' },
			{ name: 'Catalog', url: '/catalog' },
			{ name: 'Details' },
		];

		if (pathname.includes('bundle-details')) {
			newActiveSection = 'bundle-details';
			newBreadcrumbData = [{ name: 'Home', url: '/' }, { name: 'Catalog', url: '/catalog' }, { name: 'Details', url: `/catalog/details/${id}` }, { name: 'Bundle Details' }];
		}
		if (pathname.includes('methods')) {
			newActiveSection = 'methods';
			newBreadcrumbData = [{ name: 'Home', url: '/' }, { name: 'Catalog', url: '/catalog' }, { name: 'Details', url: `/catalog/details/${id}` }, { name: `${uiMenuConfig?.methods?.label || 'Methods'}` }];
		}
		if (pathname.includes('pricing')) {
			newActiveSection = 'pricing';
			newBreadcrumbData = [{ name: 'Home', url: '/' }, { name: 'Catalog', url: '/catalog' }, { name: 'Details', url: `/catalog/details/${id}` }, { name: `${uiMenuConfig?.pricing?.label || 'Pricing'}` }];
		}
		if (pathname.includes('attachments')) {
			newActiveSection = 'attachments';
			newBreadcrumbData = [{ name: 'Home', url: '/' }, { name: 'Catalog', url: '/catalog' }, { name: 'Details', url: `/catalog/details/${id}` }, { name: `${uiMenuConfig?.attachments?.label || 'Attachments'}` }];
		}
		if (pathname.includes('documentation')) {
			newActiveSection = 'documentation';
			newBreadcrumbData = [{ name: 'Home', url: '/' }, { name: 'Catalog', url: '/catalog' }, { name: 'Details', url: `/catalog/details/${id}` }, { name: `${uiMenuConfig?.documentation?.label || 'Documentation'}` }];
		}
		if (pathname.includes('eventdoc')) {
			newActiveSection = 'eventdoc';
			newBreadcrumbData = [{ name: 'Home', url: '/' }, { name: 'Catalog', url: '/catalog' }, { name: 'Details', url: `/catalog/details/${id}` }, { name: `${uiMenuConfig?.documentation?.label || 'Documentation'}` }];
		}
		if (pathname.includes('contact-provider')) {
			newActiveSection = 'contact-provider';
			newBreadcrumbData = [{ name: 'Home', url: '/' }, { name: 'Catalog', url: '/catalog' }, { name: 'Details', url: `/catalog/details/${id}` }, { name: `${uiMenuConfig?.contactProvider?.label || 'Contact Provider'}` }];
		}
		if (pathname.includes('contact-provider/')) {
			newActiveSection = 'contact-provider';
			newBreadcrumbData = [{ name: 'Home', url: '/' }, { name: 'Catalog', url: '/catalog' }, { name: 'Details', url: `/catalog/details/${id}` }, { name: `${uiMenuConfig?.contactProvider?.label || 'Contact Provider'}`, url: `/catalog/details/${id}/contact-provider` }, { name: 'Details' }];
		}
		if (pathname.includes('provider-updates')) {
			newActiveSection = 'provider-updates';
			newBreadcrumbData = [{ name: 'Home', url: '/' }, { name: 'Catalog', url: '/catalog' }, { name: 'Details', url: `/catalog/details/${id}` }, { name: `${uiMenuConfig?.providerUpdates?.label || 'Provider Updates'}` }];
		}
		if (pathname.includes('servicenow')) {
			newActiveSection = 'servicenow';
			newBreadcrumbData = [{ name: 'Home', url: '/' }, { name: 'Catalog', url: '/catalog' }, { name: 'Details', url: `/catalog/details/${id}` }, { name: `${uiMenuConfig?.serviceNow?.label || 'ServiceNow'}` }];
		}
		if (pathname.includes('get-api-access')) {
			newActiveSection = 'get-api-access';
			newBreadcrumbData = [{ name: 'Home', url: '/' }, { name: 'Catalog', url: '/catalog' }, { name: 'Details', url: `/catalog/details/${id}` }, { name: `${uiMenuConfig?.registerForAccess?.label || 'Register for Access'}` }];
		}
		if (pathname.includes('request-api-key')) {
			newActiveSection = 'request-api-key';
			newBreadcrumbData = [{ name: 'Home', url: '/' }, { name: 'Catalog', url: '/catalog' }, { name: 'Details', url: `/catalog/details/${id}` }, { name: `${uiMenuConfig?.requestApiKey?.label || 'Request API Key'}` }];
		}
		if (pathname.includes('manage-access')) {
			newActiveSection = 'manage-access';
			newBreadcrumbData = [{ name: 'Home', url: '/' }, { name: 'Catalog', url: '/catalog' }, { name: 'Details', url: `/catalog/details/${id}` }, { name: `${uiMenuConfig?.manageAccess?.label || 'Manage Access'}` }];
		}

		if (referrer === 'taxonomy') {
			newBreadcrumbData.splice(2, 0, { name: 'Taxonomy', url: '/catalog/taxonomy' });
		}
		if (referrer === 'favorites') {
			newBreadcrumbData.splice(2, 0, { name: 'Favorites', url: '/catalog/favorites' });
		}
		if (referrer === 'topapis') {
			newBreadcrumbData.splice(2, 0, { name: 'Top APIs', url: '/catalog/top-apis' });
		}
		setBreadcrumbData(newBreadcrumbData);
		setNavItem(newActiveSection);
	}, [pathname]);

	useEffect(() => {
		getData();
	}, []);

	return (
		<>
			<Breadcrumb
				data={breadcrumbData}
			/>
			{loading && (
				<SpinnerContainer />
			)}
			{!loading && !error && (
				<>
					<div className="details__grid">
						<div className="left-navigation">
							<button
								className={`${navItem === 'about' && 'is-active'}`}
								onClick={() => navigate(`../catalog/details/${id}`)}
								type="button"
							>
								{uiMenuConfig?.about?.label || 'About'}
							</button>
							{data.objectType === 'BUNDLE' && (
								<button
									className={`${navItem === 'bundle-details' && 'is-active'}`}
									onClick={() => navigate(`../catalog/details/${id}/bundle-details`)}
									type="button"
								>
									{uiMenuConfig?.bundleDetails?.label || 'Bundle Details'}
								</button>
							)}
							{data.objectType === 'SPECIFICATION' && uiMenuConfig?.methods?.enabled !== false && (
								<button
									className={`${navItem === 'methods' && 'is-active'}`}
									onClick={() => navigate(`../catalog/details/${id}/methods`)}
									type="button"
								>
									{uiMenuConfig?.methods?.label || 'Methods'}
								</button>
							)}
							{data.objectType === 'SPECIFICATION' && uiMenuConfig?.pricing?.enabled !== false && (
								<button
									className={`${navItem === 'pricing' && 'is-active'}`}
									onClick={() => navigate(`../catalog/details/${id}/pricing`)}
									disabled={!data.pricingplans?.length}
									title={!data.pricingplans?.length ? 'Currently Unavailable for this API' : ''}
									type="button"
								>
									{uiMenuConfig?.pricing?.label || 'Pricing'}
								</button>
							)}
							{uiMenuConfig?.attachments?.enabled !== false && (
								<button
									className={`${navItem === 'attachments' && 'is-active'}`}
									onClick={() => navigate(`../catalog/details/${id}/attachments`)}
									type="button"
								>
									{uiMenuConfig?.attachments?.label || 'Attachments'}
								</button>
							)}
							{data.objectType === 'SPECIFICATION' && interfaceType !== 'event' && uiMenuConfig?.documentation?.enabled !== false && (
								<button
									className={`${navItem === 'documentation' && 'is-active'}`}
									onClick={() => navigate(`../catalog/details/${id}/documentation`)}
									type="button"
								>
									{uiMenuConfig?.documentation?.label || 'Documentation'}
								</button>
							)}
							{data.objectType === 'SPECIFICATION' && interfaceType === 'event' && uiMenuConfig?.documentation?.enabled !== false && (
								<button
									className={`${navItem === 'eventdoc' && 'is-active'}`}
									onClick={() => navigate(`../catalog/details/${id}/eventdoc`)}
									type="button"
								>
									{uiMenuConfig?.documentation?.label || 'Documentation'}
								</button>
							)}
							{data.objectType === 'SPECIFICATION' && interfaceType !== 'event' && uiMenuConfig?.downloadOpenApiDocument?.enabled !== false && (
								<button
									className={`${navItem === 'pricing' && 'is-active'}`}
									onClick={e => {
										e.preventDefault();
										downloadOpenApi(`/api/web/catalog/item/${id}/openapi/file`);
									}}
									type="button"
								>
									{uiMenuConfig?.downloadOpenApiDocument?.label || 'Download OpenAPI Document'}
									{ downloading && <Icon icon="spinner" title="Downloading" spin /> }
								</button>
							)}
							{data.objectType === 'SPECIFICATION' && interfaceType === 'event' && uiMenuConfig?.downloadOpenApiDocument?.enabled !== false && (
								<button
									className={`${navItem === 'pricing' && 'is-active'}`}
									onClick={e => {
										e.preventDefault();
										downloadOpenApi(`/api/web/catalog/item/${id}/asyncapi/file`);
									}}
									type="button"
								>
									{uiMenuConfig?.downloadOpenApiDocument?.label || 'Download AsyncAPI Document'}
									{ downloading && <Icon icon="spinner" title="Downloading" spin /> }
								</button>
							)}
							{uiMenuConfig?.viewInCatalog?.enabled !== false && (
								<button
									onClick={() => viewInCatalog()}
									type="button"
								>
									{uiMenuConfig?.viewInCatalog?.label || 'View in Catalog'}
									<Icon icon="up-right-from-square" title="Open in a new window" />
								</button>
							)}
							<div className="details__navigation-divider" />
							<button
								className={`${navItem === 'contact-provider' && 'is-active'}`}
								onClick={() => navigate(`../catalog/details/${id}/contact-provider`)}
								type="button"
							>
								{uiMenuConfig?.contactProvider?.label || 'Contact Provider'}
							</button>
							<button
								className={`${navItem === 'provider-updates' && 'is-active'}`}
								onClick={() => navigate(`../catalog/details/${id}/provider-updates`)}
								type="button"
							>
								{uiMenuConfig?.providerUpdates?.label || 'Provider Updates'}
							</button>
							{uiMenuConfig?.serviceNow?.enabled !== false && (
								<button
									className={`${navItem === 'servicenow' && 'is-active'}`}
									onClick={() => navigate(`../catalog/details/${id}/servicenow`)}
									type="button"
								>
									{uiMenuConfig?.serviceNow?.label || 'ServiceNow'}
								</button>
							)}
							{data.objectType === 'SPECIFICATION' && uiMenuConfig?.registerForAccess?.enabled !== false && (
								<>
									<div className="details__navigation-divider" />
									<button
										className={`is-success ${navItem === 'register-for-access' && 'is-active'}`}
										onClick={() => navigate(`../catalog/details/${id}/register-for-access`)}
										type="button"
									>
										{uiMenuConfig?.registerForAccess?.label || 'Register for Access'}
									</button>
								</>
							)}
							{(data.objectType === 'SPECIFICATION' && customerCode === 'KP' && uiMenuConfig?.requestApiKey?.enabled !== false) && (
								<>
									<div className="details__navigation-divider" />
									<button
										className={`is-success ${navItem === 'request-api-key' && 'is-active'}`}
										onClick={() => navigate(`../catalog/details/${id}/request-api-key`)}
										type="button"
									>
										{uiMenuConfig?.requestApiKey?.label || 'Request API Key'}
									</button>
								</>
							)}
							{data.objectType === 'SPECIFICATION' && displayConfig?.apiAccess === true && (
								<>
									<div className="details__navigation-divider" />
									<button
										className={`is-success ${navItem === 'manage-access' && 'is-active'}`}
										onClick={() => navigate(`../catalog/details/${id}/manage-access`)}
										type="button"
									>
										Manage Access
									</button>
								</>
							)}
						</div>
						<div>
							<div className="flex-row justify-space-between align-items-flex-start">
								<h2>{data.name}</h2>
								<div className="flex-row align-items-center">
									{isSubscribed ? (
										<ButtonIcon
											icon="bell"
											title="Unsubscribe"
											type="success"
											classes="margin-right-8"
											onClick={() => unsubscribeUpdates()}
										/>
									) : (
										<ButtonIcon
											icon="bell"
											title="Subscribe to Updates"
											classes="margin-right-8"
											onClick={() => subscribeUpdates()}
										/>
									)}
									{isFavourite ? (
										<ButtonIcon
											icon="heart-circle-minus"
											title="Remove from Favorites"
											type="success"
											classes="margin-right-8"
											onClick={() => removeFavourite()}
										/>
									) : (
										<ButtonIcon
											icon="heart-circle-plus"
											title="Add to Favorites"
											classes="margin-right-8"
											onClick={() => addFavourite()}
										/>
									)}
									<StatusIndicator state={data.state} />
								</div>
							</div>
							<Outlet context={{
									data,
									storefrontData,
									uiMenuConfig,
									uiGeneralConfig,
								}}
							/>
						</div>
					</div>
				</>
			)}
			{error && (
				<>
					<h2>Oops! Something went wrong</h2>
					<p className="large-text margin-bottom-32">Please refresh the page and try again, if the problem persists please contact your system administrator.</p>
				</>
			)}
		</>
	);
};

export default Details;
